<template>
  <fts-popper
    :tag="tag"
    :append-to-body="appendToBody"
    :delay-on-mouse-over="delayOnMouseOver"
    :delay-on-mouse-out="delayOnMouseOut"
    :trigger="trigger"
    :placement="placement"
    :strategy-fixed="strategyFixed"
    :prevent-overflow-alt-axis="preventOverflowAltAxis"
    :prevent-overflow-tether-off="preventOverflowTetherOff"
    :prevent-overflow-boundary="preventOverflowBoundary"
    :flip="flip"
    :offset="offset"
    :content-class="getContentClass"
    :stop-propagation="stopPropagation"
    :prevent-default="preventDefault"
    :stop-propagation-sibling-class="stopPropagationSiblingClass"
    :stop-propagation-class="stopPropagationClass"
    :before-open="beforeOpen"
    :disabled="disabled"
    :z-index="zIndex"
    class="fts-dropdown-select"
    @select="selectOption"
    @created="created"
    @show="show"
    @hide="hide">
    <!-- content -->
    <template slot-scope="{select}">
      <!-- menu -->
      <slot name="menu" :select="select" defaultOptionClass="dropdown-select-option">
        <template v-if="options && options.length">
          <template v-for="(option, index) in options">
            <!-- divider -->
            <div 
              class="dropdown-select-divider" 
              :key="`divider-${index}`" 
              v-if="option.optionRole === 'divider'" />
            <!-- label -->
            <div 
              class="dropdown-select-label" 
              :key="`label-${index}`" 
              v-else-if="option.optionRole === 'label'">
              {{option.name || 'Label'}}
            </div>
            <!-- option -->
            <slot 
              name="option" 
              :select="select" 
              :option="option"
              defaultClass="dropdown-select-option" 
              v-else>
              <div class="dropdown-select-option" :key="option.id" @click="select(option)">
                <div>{{getOption(option)}}</div>
                <div class="option-description" v-if="getOptionDescription(option)">
                  {{getOptionDescription(option)}}
                </div>
              </div>
            </slot>
          </template>
        </template>
        <!-- no result -->
        <slot name="no-option" defaultClass="dropdown-select-option disabled" v-else>
          <div class="dropdown-select-option disabled">
            No available options found    
          </div>
        </slot>
      </slot>
    </template>
    <!-- trigger -->
    <div slot="reference" class="dropdown-select-toggle" :class="getToggleClass">
      <slot name="toggle" :selectedOption="selectedOption" :selectedOptionValue="selectedOptionValue">
        {{selectedOptionValue}}
      </slot>
    </div>
  </fts-popper>
</template>

<script>
import FTSPopper from "./FTSPopperNew";

export default {  
  props: {
    // popper props
    tag: String,
    appendToBody: Boolean,
    delayOnMouseOver: {
      type: Number,
      default: 10
    },
    delayOnMouseOut: {
      type: Number,
      default: 10
    },
    trigger: {
      type: String,
      default: "click",
    },
    placement: {
      type: String,
      default: "bottom-start"
    },
    strategyFixed: Boolean,
    preventOverflowAltAxis: Boolean,
    preventOverflowTetherOff: Boolean,
    preventOverflowBoundary: String,
    flip: {
      type: Boolean,
      default: true,
    },
    offset: {
      type: Array,
      default: () => [0, 6],
    },
    contentClass: String,
    appendToggleClass: String,
    appendContentClass: String,
    zIndex: Number,
    stopPropagation: Boolean,
    preventDefault: Boolean,
    stopPropagationSiblingClass: Boolean,
    stopPropagationClass: [String, Number],
    beforeOpen: Function,
    disabled: Boolean,
    // dropdown select props
    selectedOption: [Object, Number, String],
    placeholder: {
      type: String,
      default: "Select option"
    },
    options: Array,
    optionKey: {
      type: String,
      default: "name",
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    reduceKey: {
      type: String,
      default: 'id',
    },
    optionDescription: Boolean,
    optionDescriptionKey: {
      type: String,
      default: "description",
    },
  },
  model: {
    prop: 'selectedOption',
    event: 'updateSelectedOption'
  },
  components: {
    "fts-popper": FTSPopper,
  },
  methods: {
    // events
    selectOption(data) {
      let res = this.reduce(data);
      this.$emit('selected', res),
      this.$emit('updateSelectedOption', res);
    },
    created() {
      this.$emit('created');
    },
    show() {
      this.$emit('show');
    },
    hide() {
      this.$emit('hide');
    },
    // option
    getOption(item) {
      return (item instanceof Object && item.constructor.name === "Object") ? item[this.optionKey] || '(undefined)' : item;
    },
    getOptionDescription(item) {
      return this.optionDescription && item[this.optionDescriptionKey];
    },
  },
  computed: {
    getToggleClass() {
      // to:do disabled handle
      let toggleClass = this.trigger === "hover" ? "hoverable" : "clickable";
      if (this.appendToggleClass)
        toggleClass = `${toggleClass} ${this.appendToggleClass}`;
      
      return toggleClass;
    },
    getContentClass() {
      return this.contentClass ? this.contentClass : `dropdown-select-content dropdown-select-menu ${this.appendContentClass ? this.appendContentClass : ''}`;
    },
    selectedOptionValue() {
      if (this.selectedOption) { 
        if (typeof this.selectedOption === "object" && this.selectedOption[this.optionKey]) {
          return this.selectedOption[this.optionKey];     
        } else if (typeof this.selectedOption !== "object") {
          try {
            const res = this.options && this.options.find(x => x[this.reduceKey] === this.selectedOption);
            if (res && res[this.optionKey]) return res[this.optionKey];
          } catch (err) {
            console.error("DropdownSelect:SelectedOptionValue", err);
          }
          return this.selectedOption;
        }
      }  
      return this.placeholder;
    },
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.dropdown-select-toggle {
  &.hoverable {
    cursor: default;
  }
  &.clickable {
    cursor: pointer;
  }
}
.dropdown-select-content.dropdown-select-menu {
  // default
  max-height: 500px;
  max-width: 250px;
}

.dropdown-select-divider {
  margin: .475rem 0;
  border-top: 1px solid var(--fts-gray-3);
  cursor: default;
}
.dropdown-select-label {
  padding: .275rem .475rem;
  font-size: .75rem;
  color: var(--fts-gray-5);
  cursor: default;
}
.dropdown-select-option {
  display: block;
  padding: .375rem .75rem;
  word-break: break-word;
  color: var(--fts-text);
  text-decoration: none;
  &.disabled {
    color: var(--fts-gray-5);
    cursor: default;
  }
  &:hover:not(.disabled) {
    background: var(--fts-gray-2);
    cursor: pointer;
  }
  .option-description {
    margin-top: .135rem;
    font-size: 90%;
    color: var(--fts-gray-5);
  }
}
</style>